import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useRef } from "react";
import { appConfig } from "src/appConfig";
import { countryName } from "../../countryName";
import type { StepProps } from "../../hooks/usePixiWizardState";
import { BillingSection } from "./components/BillingSection";
import { PaymentSection } from "./components/PaymentSection";
import { ShippingSection } from "./components/ShippingSection";
import { ShippingWarning } from "./components/ShippingWarning";
import { useFocusFirstVerifyIssue } from "./hooks/useFocusFirstVerifyIssue";
import { usePaymentShippingForm } from "./hooks/usePaymentShippingForm";
import { FieldAttentionStyle, StepLayout } from "./styles";

/**
 * Initialise Stripe once at module scope so the `Stripe` object is not
 * recreated on every render (mirrors `src/billinglatest/index.tsx`).
 */
const stripePromise = loadStripe(appConfig.stripePublicKey);

/**
 * Payment & Shipping wizard step. Owns the Stripe `Elements` provider and the
 * form hook, then composes the payment, billing and shipping sections. All form
 * logic lives in {@link usePaymentShippingForm} and each section is its own
 * component, so this file stays a thin composition shell.
 */
export function PaymentShippingStep({ state, updateState }: StepProps) {
  const form = usePaymentShippingForm({ state, updateState, countryName });

  // On a failed Continue verification, scroll+focus+shake the first bad field.
  const stepRef = useRef<HTMLDivElement>(null);
  useFocusFirstVerifyIssue(stepRef, state.addressVerifyIssues);

  return (
    <Elements stripe={stripePromise}>
      <FieldAttentionStyle />
      <StepLayout ref={stepRef} data-testid="payment-shipping-step">
        <PaymentSection
          paymentMethod={form.paymentMethod}
          onSelectMethod={form.setPaymentMethod}
          onCardPaymentMethodChange={form.setCardPaymentMethodId}
        />

        <BillingSection
          values={form.billing}
          onChange={form.updateBilling}
          onBlur={(field) => form.markTouched("billing", field)}
          error={(field) => form.errorReason("billing", field)}
          useSameAddress={form.useSameAddress}
          onToggleSameAddress={form.setUseSameAddress}
          showSameAddressToggle={form.showSameAddressToggle}
          poNumber={form.poNumber}
          vatNumber={form.vatNumber}
          onPoNumberChange={form.setPoNumber}
          onVatNumberChange={form.setVatNumber}
          billingCountry={form.billingCountry}
          showVatInput={form.showVatInput}
          showTaxBanner={form.showTaxBanner}
        />

        {form.showShippingForm && (
          <ShippingSection
            values={form.shipping}
            onChange={form.updateShipping}
            onBlur={(field) => form.markTouched("shipping", field)}
            error={(field) => form.errorReason("shipping", field)}
            warning={
              form.showUnsupportedWarning ? (
                <ShippingWarning countryName={form.unsupportedCountryName} />
              ) : null
            }
            disabled={form.shippingDisabled}
            shippingServices={form.shippingServiceOptions}
            shippingService={form.shippingService}
            onShippingServiceChange={form.setShippingService}
          />
        )}
      </StepLayout>
    </Elements>
  );
}
